import algoliasearch from "algoliasearch/lite.js";
import {
  getCategorySlug,
  normalizeCategoryLookupKey,
  normalizeCategoryName,
} from "./category-slug.js";
import "./search-category-results.scss";
import { decode } from "html-entities";
import { getSupplierUrl } from "./supplier-url.js";
import {
  getMatchingSupplierCategories,
  supplierCategoryMatchesCategory,
} from "./supplier-category-matching.js";
import {
  getMainCategoryFacetEntries,
  PRODUCT_CATEGORY_COUNT_FACETS,
} from "./product-category-facets.js";

// Build the search-by-category page from Algolia category facets and metadata.
const SEARCH_RESULTS_PATH = "/search/";
const DEFAULT_SUBCATEGORY_LIMIT = 8;
const MAX_SUBCATEGORY_LIMIT = 1000;
const RELATED_SUPPLIERS_LIMIT = 1000;
let supplierCategoryFacetCache;

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const CATEGORY_DESCRIPTION_WORD_LIMIT = 20;

const trimWords = (value = "", limit = CATEGORY_DESCRIPTION_WORD_LIMIT) => {
  const words = String(value)
    .replace(/<[^>]*>/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length <= limit) {
    return words.join(" ");
  }

  return `${words.slice(0, limit).join(" ")}...`;
};

const getQueryFromUrl = () => {
  const params = new URLSearchParams(window.location.search);

  return (
    params.get("[query]") ||
    params.get("%5Bquery%5D") ||
    params.get("query") ||
    params.get("s") ||
    ""
  ).trim();
};

const getCategoryDisplayTitle = (name, metadata = {}) =>
  normalizeCategoryName(metadata.title || name);

const getCategoryDisplayDescription = (description = "") =>
  trimWords(normalizeCategoryName(description));

const getSearchUrl = (query) =>
  `${SEARCH_RESULTS_PATH}?[query]=${encodeURIComponent(query)}`;

const getSupplierDirectoryUrl = (root) =>
  root?.dataset?.supplierDirectoryUrl || "/supplier-directory/";

const getProductCategorySearchUrl = (categoryName, query) =>
  `/product-category/${getCategorySlug(categoryName)}/?products[query]=${encodeURIComponent(
    query,
  )}`;

const getClient = () => {
  if (!algolia?.application_id || !algolia?.search_api_key) {
    return null;
  }

  return algoliasearch(algolia.application_id, algolia.search_api_key);
};

const getFacetFilter = (attribute, value) =>
  `${attribute}:${String(value || "").replace(/^-/, "\\-")}`;

const getSupplierResultKey = (supplier) =>
  supplier.objectID || supplier.key || supplier.title || "";

const getSupplierCategoryRank = (supplier, categories) => {
  const supplierCategories = Array.isArray(supplier?.supplier_category)
    ? supplier.supplier_category
    : [];

  if (!categories.length || !supplierCategories.length) {
    return Number.MAX_SAFE_INTEGER;
  }

  const rank = categories.findIndex((category) =>
    supplierCategories.some((supplierCategory) =>
      supplierCategoryMatchesCategory(category, supplierCategory),
    ),
  );

  return rank === -1 ? Number.MAX_SAFE_INTEGER : rank;
};

const sortSuppliersByCategoryRelevance = (suppliers, categories) =>
  [...suppliers].sort((a, b) => {
    const rankDifference =
      getSupplierCategoryRank(a, categories) -
      getSupplierCategoryRank(b, categories);

    if (rankDifference !== 0) {
      return rankDifference;
    }

    return String(a?.title || "").localeCompare(String(b?.title || ""));
  });

const getCategoryMetadataMap = (hits = []) =>
  hits.reduce((metadata, hit) => {
    if (!hit?.name) {
      return metadata;
    }

    const entry = {
      title: normalizeCategoryName(hit.name),
      slug: getCategorySlug(hit.name),
      url: hit.url || "",
      imageURL: hit.sac_imageURL || hit.imageURL || "",
      description: normalizeCategoryName(
        hit.sac_short_description || hit.description || "",
      ),
      count: Number(hit.count) || 0,
    };
    const canonicalKey = normalizeCategoryLookupKey(hit.name);
    const rawKey = String(hit.name).trim().toLowerCase();

    metadata[canonicalKey] = entry;

    if (rawKey && rawKey !== canonicalKey) {
      metadata[rawKey] = entry;
    }

    return metadata;
  }, {});

const appendProductsQuery = (url, query) => {
  if (!url) {
    return query ? getSearchUrl(query) : "/search/";
  }

  if (!query) {
    return url;
  }

  const separator = url.includes("?") ? "&" : "?";

  return `${url}${separator}products[query]=${encodeURIComponent(query)}`;
};

const getCategoryUrl = ({ name, query, metadata, slug }) => {
  if (metadata.url) {
    return appendProductsQuery(metadata.url, query);
  }

  if (query) {
    return getProductCategorySearchUrl(name, query);
  }

  return `/product-category/${slug}/`;
};

const renderCategoryImage = ({ imageURL, title }) => {
  if (!imageURL) {
    return "";
  }

  return `<img width="52" height="52" src="${escapeHtml(imageURL)}" class="attachment-product-category-icon size-product-category-icon" alt="${escapeHtml(title)}" loading="lazy" decoding="async" sizes="(max-width: 575px) 44px, 52px">`;
};

const splitLinks = (links) => {
  if (!Array.isArray(links) || !links.length) {
    return [[], []];
  }

  const midpoint = Math.ceil(links.length / 2);

  if (links.length <= 1) {
    return [links, []];
  }

  return [links.slice(0, midpoint), links.slice(midpoint)];
};

const formatCount = (count = 0) => Number(count || 0).toLocaleString("en-US");

const renderResultsSummary = (query, categoryCount, productCount) => {
  if (!query) {
    return "";
  }

  const categoryLabel = categoryCount === 1 ? "category" : "categories";
  const productLabel = productCount === 1 ? "product" : "products";

  return `
    <div class="search-category-results-summary" aria-live="polite">
      <p class="search-category-results-summary-query">
        <strong>Results for ‘${escapeHtml(query)}’</strong>
      </p>
      <p class="search-category-results-summary-stats">
        <strong>${escapeHtml(formatCount(categoryCount))} ${categoryLabel} · ${escapeHtml(formatCount(productCount))} ${productLabel}</strong>
      </p>
    </div>`;
};

const getSubcategoryLimit = () => {
  const configuredLimit = Number(algolia?.searchCategoryL2Limit);

  if (!Number.isFinite(configuredLimit)) {
    return DEFAULT_SUBCATEGORY_LIMIT;
  }

  return Math.min(
    MAX_SUBCATEGORY_LIMIT,
    Math.max(1, Math.floor(configuredLimit)),
  );
};

const renderSubcategoryLinks = (subcategories = [], query) => {
  const links = subcategories
    .slice(0, getSubcategoryLimit())
    .map(({ name, count, metadata = {} }) => {
      const title = getCategoryDisplayTitle(name, metadata);
      const slug = getCategorySlug(name, metadata);

      return {
        title,
        count,
        url: getCategoryUrl({ name, query, metadata, slug }),
      };
    });
  const [firstColumn, secondColumn] = splitLinks(links);
  const renderColumn = (columnLinks) =>
    columnLinks
      .map(
        (link) => `
          <a class="subcategory-link" href="${escapeHtml(link.url)}" target="_self">
            <span class="subcategory-name">${escapeHtml(link.title)}</span>
            <span class="subcategory-count">${escapeHtml(formatCount(link.count))}</span>
          </a>`,
      )
      .join("");

  if (!firstColumn.length && !secondColumn.length) {
    return "";
  }

  return `
    <div class="columns">
      <div class="column">${renderColumn(firstColumn)}</div>
      ${secondColumn.length ? `<div class="column">${renderColumn(secondColumn)}</div>` : ""}
    </div>`;
};

const renderCategoryTile = ({
  name,
  count,
  query,
  metadata = {},
  subcategories = [],
}) => {
  const title = getCategoryDisplayTitle(name, metadata);
  const slug = getCategorySlug(name, metadata);
  const filterValue = `c_${slug}`;
  const categoryUrl = getCategoryUrl({ name, query, metadata, slug });
  const productLabel = count === 1 ? "Product" : "Products";
  const description = getCategoryDisplayDescription(metadata.description);

  return `
    <div
      class="search-category-results-card"
      data-id="${escapeHtml(filterValue)}"
    >
      <div class="heading">
        <div class="image">${renderCategoryImage({ imageURL: metadata.imageURL, title })}</div>
        <div class="summary">
          <h5>
            <a class="category-title-link" href="${escapeHtml(categoryUrl)}">${escapeHtml(title)}</a>
          </h5>
          <a class="products" href="${escapeHtml(categoryUrl)}">${escapeHtml(formatCount(count))} ${productLabel}</a>
          ${description ? `<div class="description">${escapeHtml(description)}</div>` : ""}
        </div>
      </div>
      <div class="links">
        ${renderSubcategoryLinks(subcategories, query)}
      </div>
    </div>`;
};

const fetchSubcategories = async ({
  client,
  categories,
  query,
  metadataMap,
}) => {
  if (!categories.length) {
    return {};
  }

  const subcategoryLimit = getSubcategoryLimit();

  // Build SAC-style child links from the matched product set, not from ACF rows.
  const response = await client.search(
    categories.map(({ name }) => ({
      indexName: algolia.product_index_name,
      query,
      params: {
        hitsPerPage: 0,
        facets: ["category"],
        facetFilters: [getFacetFilter("main_category", name)],
        maxValuesPerFacet: Math.max(100, subcategoryLimit),
      },
    })),
  );

  return (response?.results || []).reduce((subcategoryMap, result, index) => {
    const category = categories[index];
    const mainCategoryKey = normalizeCategoryLookupKey(category?.name);
    const subcategories = Object.entries(result?.facets?.category || {})
      .filter(
        ([name, count]) =>
          name &&
          Number(count) > 0 &&
          normalizeCategoryLookupKey(name) !== mainCategoryKey,
      )
      .map(([name, count]) => ({
        name,
        count: Number(count),
        metadata: metadataMap[normalizeCategoryLookupKey(name)] || {},
      }))
      .sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }

        return getCategoryDisplayTitle(a.name, a.metadata).localeCompare(
          getCategoryDisplayTitle(b.name, b.metadata),
        );
      });

    subcategoryMap[mainCategoryKey] = subcategories;

    return subcategoryMap;
  }, {});
};

const renderFilterList = (filterContainer, categories) => {
  const filterItems = categories
    .map(({ name, count, metadata = {} }) => {
      const title = getCategoryDisplayTitle(name, metadata);
      const slug = getCategorySlug(name, metadata);
      const filterValue = `c_${slug}`;
      const id = `search_category_${slug}`;

      return `
        <li class="checkbox-list-item">
          <label class="checkbox-list-label" for="${escapeHtml(id)}">
            <input
              type="checkbox"
              class="checkbox-list-checkbox chamfr-filter-category"
              id="${escapeHtml(id)}"
              name="category[]"
              value="${escapeHtml(filterValue)}"
              data-category-name="${escapeHtml(title)}"
            >
            <span class="checkbox-list-text">
              ${escapeHtml(title)}
              <span class="checkbox-list-count">(${escapeHtml(formatCount(count))})</span>
            </span>
          </label>
        </li>`;
    })
    .join("");

  filterContainer.innerHTML = `<ul class="checkbox-list">${filterItems}</ul>`;
};

const fetchSupplierCategoryFacets = async (client) => {
  if (supplierCategoryFacetCache) {
    return supplierCategoryFacetCache;
  }

  const response = await client.search([
    {
      indexName: algolia.supplier_index_name,
      query: "",
      params: {
        hitsPerPage: 0,
        facets: ["supplier_category"],
        maxValuesPerFacet: 1000,
      },
    },
  ]);

  supplierCategoryFacetCache = Object.keys(
    response?.results?.[0]?.facets?.supplier_category || {},
  );

  return supplierCategoryFacetCache;
};

const fetchSuppliersBySupplierCategories = async ({
  client,
  supplierCategories,
}) => {
  if (!supplierCategories.length) {
    return [];
  }

  const response = await client.search([
    {
      indexName: algolia.supplier_index_name,
      query: "",
      params: {
        hitsPerPage: RELATED_SUPPLIERS_LIMIT,
        attributesToRetrieve: [
          "objectID",
          "title",
          "shop_address",
          "key",
          "supplier_category",
        ],
        facetFilters: [
          supplierCategories.map((category) =>
            getFacetFilter("supplier_category", category),
          ),
        ],
      },
    },
  ]);

  return response?.results?.[0]?.hits || [];
};

const fetchSuppliersByCategoryQueries = async ({ client, categories }) => {
  const response = await client.search([
    ...categories.map((category) => ({
      indexName: algolia.supplier_index_name,
      query: category,
      params: {
        hitsPerPage: RELATED_SUPPLIERS_LIMIT,
        attributesToRetrieve: [
          "objectID",
          "title",
          "shop_address",
          "key",
          "supplier_category",
        ],
      },
    })),
  ]);

  const suppliersById = new Map();

  (response?.results || []).forEach((result) => {
    (result?.hits || []).forEach((supplier) => {
      const key = getSupplierResultKey(supplier);

      if (key && !suppliersById.has(key)) {
        suppliersById.set(key, supplier);
      }
    });
  });

  return Array.from(suppliersById.values());
};

const fetchRelatedSuppliers = async ({ client, categories = [] }) => {
  if (!client || !algolia?.supplier_index_name) {
    return [];
  }

  if (!categories.length) {
    const response = await client.search([
      {
        indexName: algolia.supplier_index_name,
        query: "",
        params: {
          hitsPerPage: RELATED_SUPPLIERS_LIMIT,
          attributesToRetrieve: [
            "title",
            "shop_address",
            "key",
            "supplier_category",
          ],
        },
      },
    ]);

    return response?.results?.[0]?.hits || [];
  }

  const supplierCategoryFacets = await fetchSupplierCategoryFacets(client);
  const matchingSupplierCategories = getMatchingSupplierCategories(
    categories,
    supplierCategoryFacets,
  );
  const suppliers = await fetchSuppliersBySupplierCategories({
    client,
    supplierCategories: matchingSupplierCategories,
  });

  if (suppliers.length) {
    return suppliers;
  }

  return fetchSuppliersByCategoryQueries({ client, categories });
};

const renderRelatedSuppliers = ({
  supplierContainer,
  suppliers,
  supplierDirectoryUrl,
}) => {
  const supplierLinks = suppliers
    .filter((supplier) => supplier?.title)
    .sort((a, b) => String(a.title).localeCompare(String(b.title)))
    .map(
      (supplier) => `
        <li>
          <a href="${escapeHtml(getSupplierUrl(supplier, supplierDirectoryUrl))}">
            ${escapeHtml(decode(String(supplier?.title || "")))}
          </a>
        </li>`,
    )
    .join("");

  supplierContainer.innerHTML = `
    <ul class="related-suppliers-list">
      ${supplierLinks}
      <li>
        <a class="all-suppliers-link" href="${escapeHtml(supplierDirectoryUrl)}">
          All Suppliers
        </a>
      </li>
    </ul>`;
};

const renderRelatedSuppliersStatus = (
  supplierContainer,
  supplierDirectoryUrl,
) => {
  supplierContainer.innerHTML = `
    <ul class="related-suppliers-list">
      <li>
        <a class="all-suppliers-link" href="${escapeHtml(supplierDirectoryUrl)}">
          All Suppliers
        </a>
      </li>
    </ul>`;
};

const setCategoriesListContent = (
  container,
  { query, categoryCount = 0, productCount = 0 },
  bodyHtml,
) => {
  container.innerHTML =
    renderResultsSummary(query, categoryCount, productCount) + bodyHtml;
};

const bindFilters = ({
  root,
  client,
  supplierContainer,
  categoriesContainer,
  categories,
}) => {
  const checkboxes = root.querySelectorAll(".chamfr-filter-category");
  const resetButton = root.querySelector(".search-category-results-reset");
  const categoryTiles = categoriesContainer.querySelectorAll(
    ".search-category-results-card:not([data-search-category-results-status])",
  );
  const supplierDirectoryUrl = getSupplierDirectoryUrl(root);
  const categoryNames = categories
    .map(({ name, metadata = {} }) => getCategoryDisplayTitle(name, metadata))
    .filter(Boolean);
  let supplierRequestId = 0;

  const getSelectedCheckboxes = () =>
    Array.from(checkboxes)
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => ({
        name: checkbox.dataset.categoryName,
        value: checkbox.value,
      }));

  const updateRelatedSuppliers = async (selected) => {
    if (!supplierContainer) {
      return;
    }

    const requestId = ++supplierRequestId;
    const supplierCategories = selected.length
      ? selected.map((category) => category.name).filter(Boolean)
      : categoryNames;

    try {
      const suppliers = await fetchRelatedSuppliers({
        client,
        categories: supplierCategories,
      });

      if (requestId !== supplierRequestId) {
        return;
      }

      const sortedSuppliers = sortSuppliersByCategoryRelevance(
        suppliers,
        supplierCategories,
      );

      renderRelatedSuppliers({
        supplierContainer,
        suppliers: sortedSuppliers,
        supplierDirectoryUrl,
      });
    } catch (error) {
      if (requestId === supplierRequestId) {
        renderRelatedSuppliersStatus(supplierContainer, supplierDirectoryUrl);
      }
    }
  };

  const update = () => {
    const selected = getSelectedCheckboxes();
    const selectedValues = selected.map((category) => category.value);

    categoryTiles.forEach((tile) => {
      tile.style.display =
        !selectedValues.length || selectedValues.includes(tile.dataset.id)
          ? ""
          : "none";
    });

    if (resetButton) {
      resetButton.hidden = !selected.length;
    }

    updateRelatedSuppliers(selected);
  };

  checkboxes.forEach((checkbox) => {
    checkbox.addEventListener("change", update);
  });

  if (resetButton) {
    resetButton.addEventListener("click", () => {
      checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
      update();
    });
  }

  update();
};

const renderStatusCard = (message, link = "") => `
    <div class="search-category-results-card" data-search-category-results-status>
      <div class="heading">
        <div class="summary">
          <h5>${escapeHtml(message)}</h5>
        </div>
      </div>
      <div class="links">${link}</div>
    </div>`;

const renderRelatedSuppliersFallback = (supplierContainer, root) => {
  if (!supplierContainer) {
    return;
  }

  renderRelatedSuppliersStatus(
    supplierContainer,
    getSupplierDirectoryUrl(root),
  );
};

const bindMobileAccordions = (root) => {
  if (root.dataset.searchCategoryAccordionsBound === "true") {
    return;
  }

  root.dataset.searchCategoryAccordionsBound = "true";

  [
    {
      panel: root.querySelector(".search-category-results-filters"),
      toggle: root.querySelector(
        ".search-category-results-filters .search-category-results-panel-toggle",
      ),
    },
    {
      panel: root.querySelector(".search-category-results-suppliers"),
      toggle: root.querySelector(
        ".search-category-results-suppliers .search-category-results-panel-toggle",
      ),
    },
  ].forEach(({ panel, toggle }) => {
    if (!panel || !toggle) {
      return;
    }

    toggle.addEventListener("click", () => {
      const isOpen = panel.classList.toggle("is-open");

      toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    });
  });
};

const fetchCategories = async (client, query) => {
  if (!client || !algolia?.product_index_name) {
    return { categories: [], nbHits: 0 };
  }

  const requests = [
    {
      indexName: algolia.product_index_name,
      query,
      params: {
        hitsPerPage: 0,
        facets: PRODUCT_CATEGORY_COUNT_FACETS,
        maxValuesPerFacet: 100,
      },
    },
  ];

  if (algolia?.category_index_name) {
    requests.push({
      indexName: algolia.category_index_name,
      query: "",
      params: {
        hitsPerPage: 1000,
        attributesToRetrieve: [
          "name",
          "description",
          "imageURL",
          "sac_imageURL",
          "sac_short_description",
          "count",
          "url",
        ],
      },
    });
  }

  const response = await client.search(requests);
  const productResult = response?.results?.[0] || {};
  const categoryHits = response?.results?.[1]?.hits || [];
  const metadataMap = getCategoryMetadataMap(categoryHits);
  const facetEntries = getMainCategoryFacetEntries(productResult).map(
    ({ name, count }) => ({
      name,
      count,
      metadata: metadataMap[normalizeCategoryLookupKey(name)] || {},
    }),
  );

  const categories =
    facetEntries.length || query
      ? facetEntries
      : categoryHits
          .filter((hit) => hit?.name && Number(hit.count) > 0)
          .map((hit) => ({
            name: hit.name,
            count: Number(hit.count),
            metadata: metadataMap[normalizeCategoryLookupKey(hit.name)] || {},
          }));

  const subcategoryMap = await fetchSubcategories({
    client,
    categories,
    query,
    metadataMap,
  });

  const sortedCategories = categories
    .map((category) => ({
      ...category,
      subcategories: subcategoryMap[normalizeCategoryLookupKey(category.name)] || [],
    }))
    .sort((a, b) => {
      if (!query) {
        return getCategoryDisplayTitle(a.name, a.metadata).localeCompare(
          getCategoryDisplayTitle(b.name, b.metadata),
        );
      }

      if (b.count !== a.count) {
        return b.count - a.count;
      }

      return getCategoryDisplayTitle(a.name, a.metadata).localeCompare(
        getCategoryDisplayTitle(b.name, b.metadata),
      );
    });

  return {
    categories: sortedCategories,
    nbHits: Number(productResult?.nbHits || 0),
  };
};

export const initializeSearchCategoryResults = async (root) => {
  const query = getQueryFromUrl();
  const categoriesContainer = root.querySelector(
    "#chamfr-search-category-results-categories",
  );
  const filterContainer = root.querySelector(
    "#chamfr-search-category-results-filter",
  );
  const supplierContainer = root.querySelector(
    "#chamfr-search-category-results-suppliers",
  );

  if (!categoriesContainer || !filterContainer) {
    return;
  }

  bindMobileAccordions(root);

  try {
    const client = getClient();
    const { categories, nbHits } = await fetchCategories(client, query);

    if (!categories.length) {
      const message = query
        ? `No product categories found for ‘${escapeHtml(query)}’.`
        : "Product categories could not be loaded.";
      const link = query
        ? `<a class="all" href="${escapeHtml(getSearchUrl(query))}">View Search Results &gt;</a>`
        : `<a class="all" href="${escapeHtml(root.dataset.searchUrl || "/search/")}">Search Products &gt;</a>`;

      setCategoriesListContent(
        categoriesContainer,
        { query, categoryCount: 0, productCount: nbHits },
        renderStatusCard(message, link),
      );
      renderRelatedSuppliersFallback(supplierContainer, root);
      return;
    }

    renderFilterList(filterContainer, categories);
    setCategoriesListContent(
      categoriesContainer,
      { query, categoryCount: categories.length, productCount: nbHits },
      categories
        .map((category) => renderCategoryTile({ ...category, query }))
        .join(""),
    );
    bindFilters({
      root,
      client,
      supplierContainer,
      categoriesContainer,
      categories,
    });
  } catch (error) {
    setCategoriesListContent(
      categoriesContainer,
      { query, categoryCount: 0, productCount: 0 },
      renderStatusCard(
        "Search categories could not be loaded.",
        `<a class="all" href="${escapeHtml(getSearchUrl(query))}">View Search Results &gt;</a>`,
      ),
    );
    renderRelatedSuppliersFallback(supplierContainer, root);
  }
};
